import cooperplaceApi from 'services/apis/cooperplace';

const DEFAULT_PER_PAGE = 15;
const EXPORT_FILENAME = 'solicitacoes-cadastro.xlsx';

export class ShippingCompanyRequestError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = 'ShippingCompanyRequestError';
    this.status = status;
  }
}

function buildFilterParams({ status, statusSlug, shippingDocument, email, userDocument }) {
  const params = {};

  const document = (shippingDocument || '').replace(/\D/g, '');
  const trimmedEmail = (email || '').trim();
  const trimmedUserDocument = (userDocument || '').trim();

  if (status) params.status = status;
  if (statusSlug) params.statusSlug = statusSlug;
  if (document) params.shippingDocument = document;
  if (trimmedEmail) params.email = trimmedEmail;
  if (trimmedUserDocument) params.userDocument = trimmedUserDocument;

  return params;
}

function buildListParams(filters) {
  return {
    page: filters.page || 1,
    perPage: filters.perPage || DEFAULT_PER_PAGE,
    ...buildFilterParams(filters),
  };
}

function getErrorMessage(error, defaultMessage) {
  const data = error?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  if (Array.isArray(data?.errors) && typeof data.errors[0] === 'string') return data.errors[0];
  return defaultMessage;
}

// Devolve a resposta inteira: a exportação precisa dos headers, as demais chamadas só do corpo.
async function rawRequest(call, defaultMessage) {
  try {
    return await call();
  } catch (error) {
    throw new ShippingCompanyRequestError(
      await getBlobErrorMessage(error, defaultMessage),
      error?.response?.status ?? null,
    );
  }
}

async function request(call, defaultMessage) {
  const response = await rawRequest(call, defaultMessage);
  return response.data;
}

// O header só chega ao JS se o backend o publicar em Access-Control-Expose-Headers; sem ele, vale o padrão.
function parseFilename(contentDisposition) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition || '');
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

// Com responseType 'blob', o corpo de erro também vem como Blob: lê como texto antes de extrair a mensagem.
async function getBlobErrorMessage(error, defaultMessage) {
  const data = error?.response?.data;
  if (!(data instanceof Blob)) return getErrorMessage(error, defaultMessage);

  try {
    const parsed = JSON.parse(await data.text());
    return getErrorMessage({ response: { data: parsed } }, defaultMessage);
  } catch {
    return defaultMessage;
  }
}

export async function listShippingCompanyRequests(filters = {}) {
  const params = buildListParams(filters);

  const data = await request(
    () => cooperplaceApi.get('shipping-company-request', { params }),
    'Não foi possível carregar as solicitações',
  );

  const meta = data?.meta || {};
  const total = Number(meta.total) || 0;
  const perPage = Number(meta.perPage ?? meta.per_page) || params.perPage;
  const lastPage =
    Number(meta.lastPage ?? meta.last_page) || Math.max(1, Math.ceil(total / perPage));

  return {
    rows: data?.data || [],
    total,
    lastPage,
    counts: data?.counts || [],
  };
}

// Exporta sempre o conjunto filtrado inteiro: sem paginação na query (RN-010).
export async function exportShippingCompanyRequests(filters = {}) {
  const response = await rawRequest(
    () =>
      cooperplaceApi.get('shipping-company-request/export', {
        params: buildFilterParams(filters),
        responseType: 'blob',
      }),
    'Não foi possível exportar as solicitações. Tente novamente.',
  );

  return {
    blob: response.data,
    filename: parseFilename(response.headers?.['content-disposition']) || EXPORT_FILENAME,
  };
}

export function getShippingCompanyRequest(id) {
  return request(
    () => cooperplaceApi.get(`shipping-company-request/${id}`),
    'Não foi possível carregar a solicitação. Tente novamente.',
  );
}

export function startShippingCompanyRequestAnalysis(id, { fromStatus }) {
  return request(
    () => cooperplaceApi.post(`shipping-company-request/${id}/start-analysis`, { fromStatus }),
    'Não foi possível pegar a solicitação para análise. Tente novamente.',
  );
}

export function approveShippingCompanyRequest(id, body) {
  return request(
    () => cooperplaceApi.post(`shipping-company-request/${id}/approve`, body),
    'Erro ao aprovar solicitação. Tente novamente.',
  );
}

export function rejectShippingCompanyRequest(id, { fromStatus, reason }) {
  return request(
    () => cooperplaceApi.post(`shipping-company-request/${id}/reject`, { fromStatus, reason }),
    'Não foi possível reprovar a solicitação. Tente novamente.',
  );
}

export function reopenShippingCompanyRequest(id, { fromStatus }) {
  return request(
    () => cooperplaceApi.post(`shipping-company-request/${id}/reopen`, { fromStatus }),
    'Não foi possível voltar a solicitação para análise. Tente novamente.',
  );
}

export async function listShippingCompanyRequestAudits(id) {
  const data = await request(
    () => cooperplaceApi.get(`shipping-company-request/${id}/audits`),
    'Não foi possível carregar a auditoria. Tente novamente.',
  );
  return Array.isArray(data?.data) ? data.data : [];
}
